import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Alert } from 'react-bootstrap';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import './ContactUs.css';

const ContactUs = () => {
    const { lang } = useLanguage();
    const [loadTime] = useState(Date.now());
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [status, setStatus] = useState({ type: '', msg: '' });

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        company: '',
        website: '',
        message: '',
        hp_field: ''
    });

    const content = {
        en: {
            title: "Let us know how we can help!",
            subtitle: "Having trouble signing up for classes or just have a question to ask us, feel free to let us know!",
            labelName: "NAME",
            labelEmail: "EMAIL ADDRESS",
            labelPhone: "PHONE NUMBER",
            labelCompany: "COMPANY (OPTIONAL)",
            labelWebsite: "WEBSITE (OPTIONAL)",
            labelMessage: "MESSAGE",
            btnSend: "Send Message",
            sending: "Sending...",
            success: "Thank you! Your message has been received.",
            error: "Technical error. Please try again later.",
            office: "Our Office",
            hours: "Business Hours",
            location: "27F Shinjuku i Land Tower, 6-5-1 Nishishinjuku, Shinjuku-Ku Tokyo, Japan 163-1327",
            monFri: "Mon-Fri",
            sat: "Sat",
            sun: "Sun: Closed",
        },
        jp: {
            title: "お気軽にお問い合わせください",
            subtitle: "クラスへの申し込みでお困りの方や、ご質問がある方は、こちらからお問い合わせください。",
            labelName: "お名前",
            labelEmail: "メールアドレス",
            labelPhone: "電話番号",
            labelCompany: "会社名（任意）",
            labelWebsite: "ウェブサイト（任意）",
            labelMessage: "メッセージ",
            btnSend: "メッセージを送信",
            sending: "送信中...",
            success: "ありがとうございます。メッセージを受け付けました。",
            error: "エラーが発生しました。後でもう一度お試しください。",
            office: "所在地",
            hours: "営業時間",
            location: "〒163-1327 東京都新宿区西新宿6丁目5-1 新宿アイランドタワー 27F",
            monFri: "月曜 - 金曜",
            sat: "土曜",
            sun: "日曜: 定休日",
        }
    };

    const t = content[lang];

    const handleInputChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setStatus({ type: '', msg: '' });
        setIsSubmitting(true);

        // 1. Spam Protection
        const timeTaken = (Date.now() - loadTime) / 1000;
        // If the hidden filed if filled or send too fast (less than 4 seconds)
        if (formData.hp_field !== "" || timeTaken < 4) {
            console.warn("Bot detected.");
            // Simulate success so the bot thinks he passed, we don't send anything
            setTimeout(() => {
                setStatus({ type: 'success', msg: t.success });
                setFormData({ name: '', email: '', phone: '', company: '', website: '', message: '', hp_field: '' });
                setIsSubmitting(false);
            }, 1000);
            return;
        }

        try {
            // 2. Send to both Php (Email) and to dashbord
            const [phpResponse, nodeResponse] = await Promise.allSettled([
                fetch("/send-contact.php", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(formData),
                }),
                // fetch("http://localhost:5000/api-portal/messages", {
                fetch("/api-portal/messages", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        name: formData.name,
                        email: formData.email,
                        subject: `Inquiry from ${formData.name} (${formData.company || 'Private'})`,
                        message: `Phone: ${formData.phone}\nWebsite: ${formData.website}\n\nMessage: ${formData.message}`
                    }),
                })
            ]);

            const nodeData = nodeResponse.status === 'fulfilled' ? await nodeResponse.value.json() : { success: false };

            if (nodeData.success) {
                setStatus({ type: 'success', msg: t.success });
                setFormData({ name: '', email: '', phone: '', company: '', website: '', message: '', hp_field: '' });
            } else {
                throw new Error();
            }
        } catch (err) {
            setStatus({ type: 'danger', msg: t.error });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="contact-us-page">
            {/* Hero Section */}
            <section className="contact-hero" >
                <Container>
                    <div className="text-center text-white animate-in">
                        <h1 className="display-4 fw-bold">{t.title}</h1>
                        <div className="small-red-line mx-auto"></div>
                        <p className="lead mx-auto" style={{ maxWidth: '700px' }}>{t.subtitle}</p>
                    </div>
                </Container>
            </section>

            <Container className="py-5 mt-n5">
                <Row className="g-5">
                    {/* Form left side */}
                    <Col lg={7}>
                        <div className="contact-form-card shadow-lg p-4 p-md-5">
                            {status.msg && <Alert variant={status.type}>{status.msg}</Alert>}
                            <Form onSubmit={handleSubmit}>
                                {/* Honeypot - Hidden from humans */}
                                <div style={{ display: 'none' }}>
                                    <input name="hp_field" value={formData.hp_field} onChange={handleInputChange} tabIndex="-1" autoComplete="off" />
                                </div>

                                <Row>
                                    <Col md={6} className="mb-3">
                                        <Form.Label>{t.labelName}</Form.Label>
                                        <Form.Control
                                            name="name"
                                            required
                                            value={formData.name}
                                            onChange={handleInputChange}
                                            className="custom-input-field"
                                        />
                                    </Col>
                                    <Col md={6} className="mb-3">
                                        <Form.Label>{t.labelEmail}</Form.Label>
                                        <Form.Control
                                            type="email"
                                            name="email"
                                            required
                                            value={formData.email}
                                            onChange={handleInputChange}
                                            className="custom-input-field"
                                        />
                                    </Col>
                                    <Col md={6} className="mb-3">
                                        <Form.Label>{t.labelPhone}</Form.Label>
                                        <Form.Control
                                            name="phone"
                                            required
                                            value={formData.phone}
                                            onChange={handleInputChange}
                                            className="custom-input-field"
                                        />
                                    </Col>
                                    <Col md={6} className="mb-3">
                                        <Form.Label>{t.labelCompany}</Form.Label>
                                        <Form.Control
                                            name="company"
                                            value={formData.company}
                                            onChange={handleInputChange}
                                            className="custom-input-field"
                                        />
                                    </Col>
                                    <Col md={12} className="mb-3">
                                        <Form.Label>{t.labelWebsite}</Form.Label>
                                        <Form.Control
                                            name="website"
                                            value={formData.website}
                                            onChange={handleInputChange}
                                            className="custom-input-field"
                                        />
                                    </Col>
                                    <Col md={12} className="mb-4">
                                        <Form.Label>{t.labelMessage}</Form.Label>
                                        <Form.Control
                                            name="message"
                                            as="textarea"
                                            rows={5}
                                            required
                                            value={formData.message}
                                            onChange={handleInputChange}
                                            className="custom-input-field"
                                        />
                                    </Col>
                                    <Col md={12}>
                                        <Button type="submit" className="btn-send-message px-5 py-3 text-uppercase fw-bold" disabled={isSubmitting}>
                                            {isSubmitting ? t.sending : t.btnSend}
                                        </Button>
                                    </Col>
                                </Row>
                            </Form>
                        </div>
                    </Col>

                    {/* Info right side */}
                    <Col lg={5}>
                        <div className="contact-info-sidebar">
                            <div className="info-box mb-4">
                                <h4 className="mb-4 text-black">{t.office}</h4>
                                <div className="d-flex mb-3">
                                    <MapPin className="text-red me-3" />
                                    <p>{t.location}</p>
                                </div>
                                <div className="d-flex mb-3">
                                    <Phone className="text-red me-3" />
                                    <p>03-6773-4452</p>
                                </div>
                                <div className="d-flex mb-3">
                                    <Mail className="text-red me-3" />
                                    <p>learn@level9.co.jp</p>
                                </div>
                            </div>

                            <div className="info-box">
                                <h4 className="mb-4 text-black">{t.hours}</h4>
                                <div className="d-flex">
                                    <Clock className="text-red me-3" />
                                    {/* <p>Mon - Fri: 9:00 - 18:00<br />Sat: 9:00 - 17:00<br />Sun: Closed</p> */}
                                    <p>{t.monFri}: 09:00-18:00 {t.sat}: 09:00-17:00<br /><span>{t.sun}</span></p>
                                </div>
                            </div>
                        </div>
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

export default ContactUs;